import {
    Button,
    Card,
    Col,
    DatePicker, Divider, Dropdown, Grid, Empty,
    Form,
    Input,
    InputNumber, Menu,
    Modal,
    Popover,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag, Tooltip,
    Typography
} from "antd";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import ApiService from "../../core/service/ApiService";
import { v4 } from "uuid";
import Helper from "../../core/Helper";
import moment from "moment";
import FormDownload from "./FormDownload";
import { useModal } from "../../core/context/ModalContext";
import StateChanger from "./StateChanger";
import AppointmentFiles from "./files/AppointmentFiles";
import { EyeOutlined, SmallDashOutlined, FileTextOutlined, FormOutlined, ImportOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import AppointmentTypeSelect from "./AppointmentTypeSelect";
import CurrencySelect from "../../core/components/CurrencySelect";
import { Currency } from "../Payment/MultiplePaymentApp";
import InstitutionCreateEdit from "../institution/InstitutionCreateEdit";
import AppointmentOperationSubItemsEdit from "./AppointmentOperationSubItemsEdit";
import { ColorDot } from "../../core/components/ColorDot";
import AppConfig from "../../core/service/AppConfig";
import Borcodeprinter from "./BarcodePrinter";
import { PriceQuoteStorage } from "../../core/service/PriceQuoteStorage";
import message from "../../core/service/NotificationService";

const { Text, Title } = Typography;

const toDateTimeLocalString = (value) => {
    if (!value) return '';
    const m = moment.isMoment(value) ? value : moment(value);
    if (!m.isValid()) return '';
    return m.format('YYYY-MM-DDTHH:mm');
};

// Mobil için ayrı date + time input — datetime-local iOS Safari'de container'ı taşırıyordu
const DateTimeSplitInput = ({ value, onChange, disabled }) => {
    const m = value ? (moment.isMoment(value) ? value : moment(value)) : null;
    const dateStr = m && m.isValid() ? m.format('YYYY-MM-DD') : '';
    const timeStr = m && m.isValid() ? m.format('HH:mm') : '';

    const updateDate = (newDate) => {
        if (!newDate) {
            onChange && onChange(null);
            return;
        }
        const combined = moment(`${newDate} ${timeStr || '00:00'}`, 'YYYY-MM-DD HH:mm');
        onChange && onChange(combined.isValid() ? combined : null);
    };

    const updateTime = (newTime) => {
        const base = dateStr || moment().format('YYYY-MM-DD');
        const combined = moment(`${base} ${newTime || '00:00'}`, 'YYYY-MM-DD HH:mm');
        onChange && onChange(combined.isValid() ? combined : null);
    };

    return (
        <div style={{ display: 'flex', gap: 6, width: '100%', minWidth: 0 }}>
            <Input
                type="date"
                value={dateStr}
                onChange={(e) => updateDate(e.target.value)}
                disabled={disabled}
                size="middle"
                style={{ flex: 1, minWidth: 0 }}
            />
            <Input
                type="time"
                value={timeStr}
                onChange={(e) => updateTime(e.target.value)}
                disabled={disabled}
                size="middle"
                style={{ flex: '0 0 110px', minWidth: 0 }}
            />
        </div>
    );
};

export default function AppointmentDetailEdit({ appointmentId, onAppointmentChange, onSave, onDelete, onCancel, onPayment, onCheckedChange, onStateChanged }) {
    const formRef = useRef()
    const formOtherOperationRef = useRef();
    const screens = Grid.useBreakpoint();
    const [doctors, setDoctors] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const { firmSettings } = useUser();
    const [operationList, setOperationList] = useState([]);
    const { openModal, closeModal } = useModal()
    const { hasPermission, user } = useUser();
    const [currency, setCurrency] = useState('TRY')
    // hasQuote artık birim-aware: sadece bu randevunun birimine ait kayıt varsa true
    const [hasQuote, setHasQuote] = useState(false)
    // Bu birimle eşleşmeyen bir teklif varsa ufak uyarı için bilgi (null veya { unitName, firmName, opCount })
    const [otherUnitQuote, setOtherUnitQuote] = useState(null)
    const [operationSelectOpen, setOperationSelectOpen] = useState(false)
    const [institutionSelectOpen, setInstitutionSelectOpen] = useState(false)
    const [operationPickerOpen, setOperationPickerOpen] = useState(false)
    const [operationPickerSearch, setOperationPickerSearch] = useState('')
    const operationPickerSearchRef = useRef(null)
    // iOS mobil klavye primer'ı — user-gesture synchronous focus için
    const operationKeyboardPrimerRef = useRef(null)

    // Modal açılınca real search input'a focus transfer et (primer'dan)
    useEffect(() => {
        if (!operationPickerOpen) return;
        const timer = setTimeout(() => {
            operationPickerSearchRef.current?.focus();
        }, 250);
        return () => clearTimeout(timer);
    }, [operationPickerOpen]);

    const openOperationPicker = () => {
        // Önce primer input'a synchronous focus → iOS klavyesi açılır
        try {
            operationKeyboardPrimerRef.current?.focus({ preventScroll: true });
        } catch { /* ignore */ }
        setOperationPickerSearch('');
        setOperationPickerOpen(true);
    };

    const institutionIdRef = useRef(null)
    const appointmentTypeIdRef = useRef(null)

    const priceList = useRef([])
    const appointmentTypePriceList = useRef([])
    const appointmentTypes = useRef([])

    const [appointment, setAppointment] = useState({
        doctor: {},
        institution: {},
        unit: {},
        appointmentOperations: [],
        appointmentOtherOperations: [],
        checkIn: false,
        sendEnabiz: true,
    })

    // Başka bir yerden (header butonu, bottom bar vb.) fiyat teklifi kaydedilince banner'ı güncelle.
    // Banner yalnızca bu randevunun birimine ait kayıt varsa görünür; farklı birime ait
    // kayıt varsa ufak uyarı için ayrı state.
    useEffect(() => {
        const evaluate = () => {
            const uId = appointment?.unit?.id;
            const forUnit = PriceQuoteStorage.getForUnit(uId);
            setHasQuote(!!forUnit);

            if (forUnit) {
                setOtherUnitQuote(null);
                return;
            }
            const raw = PriceQuoteStorage.get();
            const totalOps = (raw?.operationList?.length || 0) + (raw?.otherOperations?.length || 0);
            if (raw && totalOps > 0 && uId && Number(raw.unitId) !== Number(uId)) {
                setOtherUnitQuote({
                    unitName: raw.unitName || '',
                    firmName: raw.firmName || '',
                    opCount: totalOps,
                });
            } else {
                setOtherUnitQuote(null);
            }
        };
        evaluate();
        const unsubscribe = PriceQuoteStorage.subscribe(evaluate);
        return unsubscribe;
    }, [appointment?.unit?.id]);

    const loadAppointment = async (appointmentId) => {
        if (!appointmentId)
            return

        let _appointment = {
            doctor: {},
            institution: {},
            unit: {},
            appointmentOperations: [],
            appointmentOtherOperations: [],
            startDate: moment(),
            endDate: moment().add(15, 'minutes')
        }

        if (typeof appointmentId === 'object') {
            _appointment = {
                ..._appointment,
                unit: {
                    id: appointmentId.unitId,
                    name: appointmentId.unitName,
                },
                firm: {
                    id: appointmentId.firmId,
                },
                newRecord: true,
                startDate: appointmentId.startDate,
                endDate: appointmentId.endDate,
            }
        }
        else {
            const res = await ApiService.Appointment.GetAppointment(appointmentId)
            if (res.success) {
                _appointment = res.data
                _appointment.startDate = moment(new Date(_appointment.startDate))
                _appointment.endDate = calcEndDate(_appointment.appointmentOperations, moment(new Date(_appointment.startDate)))
                _appointment.newRecord = false;
                _appointment.appointmentOperations = _appointment.appointmentOperations.map(x => {
                    return {
                        ...x,
                        subItems: JSON.parse(x.subItems ?? '[]')
                    }
                });

            }
        }

        let _doctors = []
        let _institutions = []
        let _operations = []
        await Promise.all([
            ApiService.Doctor.GetDoctorList({ unitId: _appointment?.unit?.id }).then(res => {
                _doctors = res.data
            }),
            ApiService.Institution.GetInstitutionListByFirm(_appointment?.firm?.id).then(res => {
                _institutions = res.data
            }),
            ApiService.Operation.GetOperationList(_appointment?.unit?.id).then(res => {
                _operations = res.data.map(x => ({
                    ...x,
                    subItems: JSON.parse(x.subItems ?? '[]')
                }))
            })
        ])

        const doctor = _doctors?.length > 0 && !_appointment?.doctor?.id ? _doctors[0] : _appointment.doctor
        const institution = _institutions?.length > 0 && !_appointment.institution.id ? (_institutions.find(x => x.isDefault) || _institutions[0]) : _appointment.institution
        const appointmentTypeId = _appointment.appointmentTypeId || institution?.appointmentType?.id

        await loadInstitutionPriceList(institution.id, _operations)
        await loadAppointmentTypePriceList(appointmentTypeId, _operations)

        setDoctors(_doctors)
        setInstitutions(_institutions)
        setOperationList(_operations)
        setAppointment({
            ..._appointment,
            doctor,
            institution,
            appointmentTypeId
        })
        setCurrency(_appointment.currency || 'TRY')

        formRef.current.setFieldsValue({
            doctorId: doctor?.id,
            institutionId: institution?.id,
            checkIn: false,
            anamnez: _appointment.anamnez,
            provisionNo: _appointment.provisionNo,
            note: _appointment.note,
            privateNote: _appointment.privateNote,
            startDate: _appointment.startDate,
            endDate: _appointment.endDate,
            appointmentTypeId: appointmentTypeId
        })
    }

    const calcEndDate = (operations, startDate) => {
        if (operations && operations.length > 0) {
            let sum = 0;
            operations.forEach(op => sum += op.processMinute)
            return moment(startDate).add(sum, 'minutes');
        }
        else {
            return moment(startDate).add(15, 'minutes');
        }
    }

    const onSelectOperation = async (id) => {
        const index = appointment.appointmentOperations.findIndex(x => x.operationId === id);
        if (index > -1)
            return;

        const operation = operationList.find(x => x.id === id);
        const institutionPrice = priceList.current.find(x => x.operationId === id)?.price
        const appTypePrice = appointmentTypePriceList.current.find(x => x.operationId === id)?.price
        const appType = appointmentTypes.current.find(x => x.id === appointment.appointmentTypeId)

        let price = operation?.price
        if (appType?.overrideInstitutionPrice) {
            price = appTypePrice
        }
        else if (institutionPrice) {
            price = institutionPrice
        }

        const ops = [
            ...appointment.appointmentOperations,
            {
                ...operation,
                id: v4(),
                operationId: operation.id,
                priceModified: false,
                price: price,
                institutionPrice: 300,
                discount: 0,
                subItems: [],
                operation
            }
        ]

        setAppointment({
            ...appointment,
            appointmentOperations: ops
        })

        formRef.current.setFieldsValue({
            endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
        })
    }

    const getMaxDiscount = () => {
        const institution = institutions.find(x => x.id === appointment?.institution?.id);
        return Math.min(institution?.discount, user?.discount)
    }

    const updateOperationData = (id, key, value) => {
        const ops = [...appointment.appointmentOperations].map(x => {
            delete x.error;
            return x;
        });

        const index = ops.findIndex(x => x.id === id);
        if (index > -1) {
            const institutionPrice = priceList.current.find(x => x.institutionId === appointment.institution.id && x.operationId === ops[index]?.operationId)?.price
            if (key === 'price') {
                if (institutionPrice > value) {
                    ops[index]['discount'] = 100 - (value * 100 / institutionPrice)
                }
                else {
                    ops[index]['discount'] = 0
                }
            }
            else if (key === 'discount') {
                if (value > 0) {
                    ops[index]['price'] = institutionPrice - (institutionPrice * value / 100)
                }
                else {
                    ops[index]['price'] = institutionPrice;
                }
            }

            ops[index][key] = value;
        }

        setAppointment({
            ...appointment,
            appointmentOperations: ops
        });
    }

    const updateCurrency = (value) => {
        setCurrency(value)
        setAppointment({
            ...appointment,
            currency: value,
        });
    }

    const loadInstitutionPriceList = async (institutionId, operations) => {
        if (institutionIdRef.current === institutionId)
            return

        const res = await ApiService.Operation.GetInstitutionPriceList(institutionId, operations.map(x => x.id));
        if (res.success) {
            priceList.current = res.data
            institutionIdRef.current = institutionId
        }
    }

    const loadAppointmentTypePriceList = async (typeId, operations) => {
        if (appointmentTypeIdRef.current === typeId)
            return

        const res = await ApiService.Operation.GetAppointmentTypePriceList(typeId, operations.map(x => x.id));
        if (res.success) {
            appointmentTypePriceList.current = res.data
            appointmentTypeIdRef.current = typeId
        }
    }

    const deleteOtherOperation = (row) => {
        setAppointment({
            ...appointment,
            appointmentOtherOperations: appointment.appointmentOtherOperations.filter(x => x.id !== row.id),
        })
    }

    const onOtherOperationFinish = (formData) => {
        setAppointment({
            ...appointment,
            appointmentOtherOperations: [
                ...appointment.appointmentOtherOperations,
                {
                    id: v4(),
                    ...formData,
                }
            ],
        })
        formOtherOperationRef.current.resetFields()
    }

    const onStateChange = async (stateId, stateDescForm) => {
        formRef.current.setFieldsValue({
            appointmentStateId: stateId
        })

        setAppointment({
            ...appointment,
            state: {
                id: stateId
            },
            stateDesc: stateDescForm || {}
        })
    }

    const onFiles = () => {
        const component = (
            <AppointmentFiles appointmentId={appointmentId} />
        )
        openModal('', component, () => {
            onStateChanged && onStateChanged('files')
        })
    }

    const getInstitutionPrice = (operationId) => {
        return priceList.current.find(x => x.institutionId === appointment.institution.id && x.operationId === operationId)?.price
    }

    const getAppointmentTypePrice = (operationId) => {
        return appointmentTypePriceList.current.find(x => x.appointmentTypeId === appointment.appointmentTypeId && x.operationId === operationId)?.price
    }

    const getInstitutionTotalPrice = () => {
        return priceList.current?.filter(x => appointment.appointmentOperations.map(y => y.operationId).includes(x.operationId))?.reduce((prev, val) => prev += val.price, 0)
    }

    const getAppointmentTypeTotalPrice = () => {
        return appointmentTypePriceList.current?.filter(x => appointment.appointmentOperations.map(y => y.operationId).includes(x.operationId))?.reduce((prev, val) => prev += val.price, 0)
    }

    const getTotalPrice = () => {
        return appointment?.appointmentOperations?.reduce((prev, val) => prev += val.price, 0)
    }

    const operationsPrice = appointment.appointmentOperations?.reduce((prev, val) => prev += val?.operation?.price, 0) || 0;
    const extraOperationsPrice = appointment.appointmentOtherOperations?.reduce((prev, val) => prev += val.price, 0) || 0;
    const institutionPrice = getInstitutionTotalPrice();
    const specialPrice = getAppointmentTypeTotalPrice();
    const grandTotal = getTotalPrice();
    const activeCurrency = currency || 'TRY';

    const renderSummaryContent = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong style={{ fontSize: 16 }}>{t('Total')}:</Text>
                        <Text strong style={{ color: '#1890ff', fontSize: 18 }}>{Helper.Currency.format(grandTotal, { currency: activeCurrency })}</Text>
                    </div>
                    <Space split={<Divider type="vertical" />} style={{ flexWrap: 'wrap' }}>
                        <Text>{t('Operations')}: <Text strong>{Helper.Currency.format(operationsPrice)}</Text></Text>
                        {extraOperationsPrice > 0 && (
                            <Text>{t('Extra')}: <Text strong>{Helper.Currency.format(extraOperationsPrice, { currency: activeCurrency })}</Text></Text>
                        )}
                        {(institutionPrice > 0 || specialPrice > 0) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('Institution')}: {Helper.Currency.format(institutionPrice)} / {t('Special')}: {Helper.Currency.format(specialPrice)}
                            </Text>
                        )}
                    </Space>
                </div>
            </div>
        )
    }

    const getPaymentSummary = (colSpan) => {
        return (
            <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={colSpan}>
                        {renderSummaryContent()}
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        )
    }



    useEffect(() => {
        loadAppointment(appointmentId)
    }, [appointmentId]);

    useEffect(() => {
        if (appointment?.institution?.id) {
            loadInstitutionPriceList(appointment.institution.id, operationList)
            setAppointment({ ...appointment })
        }
    }, [appointment?.institution?.id]);

    useEffect(() => {
        if (appointment?.appointmentTypeId) {
            loadAppointmentTypePriceList(appointment?.appointmentTypeId, operationList)
            setAppointment({ ...appointment })
        }
    }, [appointment?.appointmentTypeId]);

    useEffect(() => {
        onAppointmentChange && onAppointmentChange(appointment)
    }, [appointment]);

    const handleSave = async () => {
        await onSave({
            ...appointment,
            endDate: calcEndDate(appointment.appointmentOperations, moment(new Date(appointment.startDate)))
        })
        if (!appointment.newRecord)
            await loadAppointment(appointmentId)
    }

    // --- FİYAT TEKLİFİ AKTARIMI ---
    const transferQuote = (mode) => {
        const currentUnitId = appointment?.unit?.id;
        const quote = PriceQuoteStorage.getForUnit(currentUnitId);
        if (!quote) {
            setHasQuote(false);
            return;
        }

        const quoteOps = (quote.operationList || []).map(op => ({
            ...op,
            id: v4(), // yeni id — appointment ops'ta çakışmasın
        }));
        const quoteOtherOps = (quote.otherOperations || []).map(op => ({
            ...op,
            id: v4(),
        }));

        const newOps = mode === 'replace'
            ? quoteOps
            : [...appointment.appointmentOperations, ...quoteOps];

        const newOtherOps = mode === 'replace'
            ? quoteOtherOps
            : [...appointment.appointmentOtherOperations, ...quoteOtherOps];

        // Kurum + randevu türünü set et (yalnızca quote bu birimle eşleşiyorsa)
        let nextInstitution = appointment.institution;
        let nextAppointmentTypeId = appointment.appointmentTypeId;
        if (quote.unitMatches) {
            if (quote.institutionId) {
                const inst = institutions.find(x => x.id === quote.institutionId);
                if (inst) {
                    nextInstitution = inst;
                }
            }
            if (quote.appointmentTypeId) {
                nextAppointmentTypeId = quote.appointmentTypeId;
            }
        }

        const newAppointment = {
            ...appointment,
            institution: nextInstitution,
            appointmentTypeId: nextAppointmentTypeId,
            appointmentOperations: newOps,
            appointmentOtherOperations: newOtherOps,
            currency: quote.currency || appointment.currency,
        };

        setAppointment(newAppointment);
        setCurrency(quote.currency || currency);

        // Form alanlarını da güncelle (institution, appointmentType, endDate)
        if (formRef.current) {
            formRef.current.setFieldsValue({
                institutionId: nextInstitution?.id,
                appointmentTypeId: nextAppointmentTypeId,
                endDate: calcEndDate(newOps, formRef.current.getFieldsValue().startDate),
            });
        }

        // Fiyat listelerini de yeni seçimlere göre tetikle
        if (quote.unitMatches) {
            if (nextInstitution?.id) {
                loadInstitutionPriceList(nextInstitution.id, operationList);
            }
            if (nextAppointmentTypeId) {
                loadAppointmentTypePriceList(nextAppointmentTypeId, operationList);
            }
        }

        // Sadece aktarılan işlemleri hafızadan çıkar (metadata + diğer birimlerin ops'u kalır)
        PriceQuoteStorage.removeTransferredForUnit(currentUnitId);
        setHasQuote(false);
        message.success(mode === 'replace' ? 'Mevcut işlemler değiştirildi' : 'Listeler birleştirildi');
    };

    const discardQuote = () => {
        Modal.confirm({
            title: 'Fiyat teklifini sil',
            content: 'Hafızadaki fiyat teklifi silinsin mi?',
            okText: t('Yes'),
            cancelText: t('No'),
            okButtonProps: { danger: true },
            onOk: () => {
                PriceQuoteStorage.clear();
                setHasQuote(false);
                message.info('Fiyat teklifi hafızadan silindi');
            }
        });
    };

    const canShowSave = appointmentId && hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT)
    const canShowPayments = !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT)
    const canShowFiles = !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT)
    const canShowFormDownload = !(typeof appointmentId === 'object')
    const canShowDelete = !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.APPOINTMENT_DELETE)
    const canShowClose = !!appointmentId

    const renderActionButtons = () => {
        if (!screens.md) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                    {/* Satır 1: Kaydet (tam genişlik) */}
                    {canShowSave && (
                        <Popover
                            content={(
                                <Typography.Text>
                                    {t('Changes to the patient card will be saved.')}
                                </Typography.Text>
                            )}
                        >
                            <Button
                                block
                                type={'primary'}
                                size={'middle'}
                                onClick={handleSave}
                            >
                                {t('Save')}
                            </Button>
                        </Popover>
                    )}

                    {/* Satır 2: Sil + Kapat */}
                    {(canShowDelete || canShowClose) && (
                        <div style={{ display: 'flex', gap: 8 }}>
                            {canShowDelete && (
                                <Button
                                    type={'primary'}
                                    size={'middle'}
                                    danger
                                    style={{ flex: 1 }}
                                    onClick={async () => { onDelete(appointment) }}
                                >
                                    {t('Delete')}
                                </Button>
                            )}
                            {canShowClose && (
                                <Button
                                    type={'warning'}
                                    size={'middle'}
                                    danger
                                    style={{ flex: 1 }}
                                    onClick={async () => { onCancel() }}
                                >
                                    {t('Close')}
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Satır 3: Ödemeler + Dosyalar + Form + Barkod */}
                    {(canShowPayments || canShowFiles || canShowFormDownload || canShowSave) && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {canShowPayments && (
                                <Button
                                    size={'middle'}
                                    className={'bg-success text-white'}
                                    style={{ flex: 1, minWidth: 0 }}
                                    onClick={onPayment}
                                >
                                    {t('Payments')}
                                </Button>
                            )}
                            {canShowFiles && (
                                <Button
                                    size={'middle'}
                                    className={'bg-success text-white'}
                                    style={{ flex: 1, minWidth: 0 }}
                                    onClick={onFiles}
                                >
                                    {t('Files')}
                                </Button>
                            )}
                            {canShowFormDownload && (
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <FormDownload
                                        appointmentId={appointmentId}
                                        unitId={appointment?.unit?.id}
                                    />
                                </div>
                            )}
                            {canShowSave && (
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Borcodeprinter appointmentId={appointmentId} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <>
                <Button.Group>
                    {
                        canShowSave && (
                            <Popover
                                content={(
                                    <Typography.Text>
                                        {t('Changes to the patient card will be saved.')}
                                    </Typography.Text>
                                )}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Button
                                        style={{ width: 60 }}
                                        size={'small'}
                                        onClick={handleSave}
                                    >
                                        {t('Save')}
                                    </Button>
                                    {/* {
                                        appointment?.state?.stateGroup?.id === 2 && hasPermission(PERMISSIONS.ENABIZ) && (
                                            <Button
                                                style={{ marginLeft: 4 }}
                                                size={'small'}
                                                type={'primary'}
                                                onClick={async () => {
                                                    const res = await ApiService.AppointmentTeletip.SendEnabizBatch(appointmentId)
                                                    if (res.success) {
                                                        await loadAppointment(appointmentId)
                                                    }
                                                }}
                                            >
                                                {t('Send Enabiz')}
                                            </Button>
                                        )
                                    } */}
                                    <Borcodeprinter
                                        appointmentId={appointmentId} />
                                </div>
                            </Popover>
                        )
                    }
                </Button.Group>
                <div style={{ flex: 1 }} />
                <Button.Group style={{ flexWrap: 'wrap' }}>
                    {
                        canShowPayments && (
                            <Button size={'small'} onClick={onPayment} className={'bg-success text-white'}
                                style={{ width: 75 }}>{t('Payments')}</Button>
                        )
                    }
                    {
                        canShowFiles && (
                            <Button size={'small'} onClick={onFiles} className={'bg-success text-white'}
                                style={{ width: 75 }}>{t('Files')}</Button>
                        )
                    }
                    {
                        canShowFormDownload && (
                            <FormDownload
                                appointmentId={appointmentId}
                                unitId={appointment?.unit?.id}
                            />
                        )
                    }
                    {
                        canShowDelete && (
                            <Button
                                style={{ width: 60 }}
                                type={'primary'}
                                size={'small'}
                                danger={true}
                                onClick={async () => {
                                    onDelete(appointment)
                                }}
                            >
                                {t('Delete')}
                            </Button>
                        )
                    }
                    {
                        canShowClose && (
                            <Button
                                style={{ width: 60 }}
                                type={'warning'}
                                size={'small'}
                                danger={true}
                                onClick={async () => {
                                    onCancel()
                                }}
                            >
                                {t('Close')}
                            </Button>
                        )
                    }
                </Button.Group>
            </>
        )
    }

    const renderMobileOperations = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                {appointment.appointmentOperations.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Operations')} />
                )}
                {appointment.appointmentOperations.map(op => {
                    const ops = operationList.find(x => x.id === op.operationId)
                    return (
                        <Card size="small"
                            type="inner"
                            key={op.id}
                            title={<span style={{ fontSize: 14, fontWeight: 500 }}>{op?.operation?.name}</span>}
                            extra={
                                <Button
                                    size={'middle'}
                                    danger
                                    disabled={
                                        Helper.Date.isBeforeToday(appointment.startDate) &&
                                        !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                    }
                                    onClick={() => {
                                        setAppointment({
                                            ...appointment,
                                            appointmentOperations: appointment.appointmentOperations.filter(x => x.id !== op.id),
                                        })
                                    }}>
                                    Sil
                                </Button>
                            }
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div>
                                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                        {t('Accession')}
                                    </Typography.Text>
                                    <Input
                                        style={{ width: '100%' }}
                                        size={'middle'}
                                        placeholder={t('Accession')}
                                        value={op.accessionNumber}
                                        onChange={(e) => {
                                            updateOperationData(op.id, 'accessionNumber', e.target.value)
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    <div style={{ flex: 1 }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                            {t('Min')}
                                        </Typography.Text>
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            size={'middle'}
                                            placeholder={t('Min')}
                                            defaultValue={op?.processMinute}
                                            value={op?.processMinute}
                                            onChange={(val) => {
                                                updateOperationData(op.id, 'processMinute', val)
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                            {t('Currency')}
                                        </Typography.Text>
                                        <TitleCurrencySelector
                                            value={currency}
                                            disabled={Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)}
                                            onChange={(e) => {
                                                updateCurrency(e)
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('Price')}</Typography.Text>
                                        {ops && ops.subItems?.length > 0 && (
                                            <Button
                                                icon={<SmallDashOutlined />}
                                                size={'small'}
                                                onClick={() => {
                                                    openModal(
                                                        t(''),
                                                        <AppointmentOperationSubItemsEdit
                                                            currency={currency}
                                                            subItems={ops.subItems}
                                                            selectedSubItems={op.subItems ?? []}
                                                            onSave={(items) => {
                                                                const updatedOps = appointment.appointmentOperations.map(x => {
                                                                    if (x.id === op.id) {
                                                                        return {
                                                                            ...x,
                                                                            price: items.reduce((prev, val) => prev += val.price, 0),
                                                                            subItems: items
                                                                        }
                                                                    }
                                                                    return x
                                                                })
                                                                setAppointment({
                                                                    ...appointment,
                                                                    appointmentOperations: updatedOps
                                                                })
                                                                closeModal()
                                                            }}
                                                        />,
                                                        () => { }
                                                    )
                                                }}
                                            >
                                                Alt İşlemler
                                            </Button>
                                        )}
                                    </div>
                                    <InputNumber
                                        disabled={
                                            Helper.Date.isBeforeToday(appointment.startDate) &&
                                            !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                        }
                                        style={{ width: '100%' }}
                                        size={'middle'}
                                        prefix={Currency[appointment.currency]}
                                        value={op?.price}
                                        step={0.01}
                                        onChange={(newPrice) => {
                                            if (ops?.subItems?.length > 0) {
                                                const updatedOperations = appointment.appointmentOperations.map(mapOp => {
                                                    if (mapOp.id === op.id) {
                                                        const subItemsPrice = mapOp.subItems.reduce((sum, item) => sum + item.price, 0)
                                                        const ratio = newPrice / subItemsPrice

                                                        mapOp.price = newPrice
                                                        mapOp.subItems = mapOp.subItems.map(sub => ({
                                                            ...sub,
                                                            price: parseFloat((sub.price * ratio).toFixed(2))
                                                        }))

                                                        const newSubItemsPrice = mapOp.subItems.reduce((sum, item) => sum + item.price, 0)
                                                        const diff = newPrice - newSubItemsPrice
                                                        if (Math.abs(diff) > 0.01 && mapOp.subItems.length > 0) {
                                                            mapOp.subItems[0].price = parseFloat((mapOp.subItems[0].price + diff).toFixed(2))
                                                        }

                                                        return { ...mapOp }
                                                    }
                                                    return mapOp
                                                })

                                                setAppointment({
                                                    ...appointment,
                                                    appointmentOperations: updatedOperations
                                                })
                                            } else {
                                                updateOperationData(op.id, 'price', newPrice)
                                            }
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#999', borderTop: '1px dashed #eee', paddingTop: 6 }}>
                                    <span>Liste: {Helper.Currency.format(op?.operation?.price)}</span>
                                    <span>{Helper.Currency.format(getInstitutionPrice(op?.operationId))} / {Helper.Currency.format(getAppointmentTypePrice(op?.operationId))}</span>
                                </div>
                            </div>
                        </Card>
                    )
                })}
                <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                    {renderSummaryContent()}
                </Card>
            </div>
        )
    }

    const renderMobileExtraOperations = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {appointment.appointmentOtherOperations.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Extra Operations')} />
                )}
                {appointment.appointmentOtherOperations.map((row, index) => (
                    <Card key={index} size="small" type="inner" title={row.name} extra={
                        <Button size={'small'} onClick={() => deleteOtherOperation(row)} danger={true}>X</Button>
                    }>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography.Text>{t('Price')}</Typography.Text>
                            <Typography.Text strong>{Helper.Currency.format(row.price, { currency })}</Typography.Text>
                        </div>
                    </Card>
                ))}
                <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                    {renderSummaryContent()}
                </Card>
            </div>
        )
    }

    // Görsel ayrıştırma için her kart farklı bir renk vurgusu (sol kenar) alıyor.
    // Mobilde özellikle alt alta dizildiklerinde bu vurgu kartları net ayırır.
    const appointmentCardAccent = '#13c2c2';   // cyan — randevu
    const operationsCardAccent = '#722ed1';    // mor — işlemler & ödemeler
    const cardCommon = {
        marginBottom: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    };

    return (
        <>
            <Card
                title={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <FileTextOutlined style={{ color: appointmentCardAccent }} />
                        <Typography.Text strong>
                            {t('Appointment')} {typeof appointmentId !== 'object' ? appointmentId : ''}: {appointment.newRecord ? t('New') : t('Edit')} - {appointment?.unit?.name}
                        </Typography.Text>
                        {screens.md && renderActionButtons()}
                    </div>
                )}
                style={{
                    backgroundColor: '#f5f5f5',
                    borderLeft: `4px solid ${appointmentCardAccent}`,
                    ...cardCommon,
                }}
                headStyle={{ background: '#f5f5f5' }}
                bodyStyle={{ background: '#f5f5f5' }}>
                <Form
                    layout={'vertical'}
                    ref={formRef}
                onValuesChange={() => {
                    const form = formRef.current.getFieldsValue()

                    const ins = institutions.find(x => x.id === form.institutionId)
                    const doctor = doctors.find(x => x.id === form.doctorId)

                    let typeId = appointment.appointmentTypeId
                    if (form.institutionId !== appointment.institution.id) {
                        typeId = ins?.appointmentType?.id
                    }
                    else {
                        typeId = form.appointmentTypeId
                    }


                    setAppointment({
                        ...appointment,
                        institution: { ...ins },
                        doctor,
                        anamnez: form.anamnez,
                        provisionNo: form.provisionNo,
                        privateNote: form.privateNote,
                        note: form.note,
                        startDate: form.startDate,
                        endDate: calcEndDate(appointment.appointmentOperations, form.startDate),
                        appointmentTypeId: typeId
                    })

                    formRef.current.setFieldsValue({
                        endDate: calcEndDate(appointment.appointmentOperations, form.startDate),
                        appointmentTypeId: typeId
                    })
                }}
            >
                <Row gutter={24}>
                    <Col xs={24} xl={12}>
                        {screens.md ? (
                            <Form.Item
                                label={t('Start Date')}
                                name={'startDate'}
                                rules={[{ required: true }]}
                            >
                                <DatePicker
                                    size={'small'}
                                    format={'DD-MM-YYYY HH:mm'}
                                    style={{ width: '100%' }}
                                    showTime={true}
                                    locale={Helper.Date.getLocale()}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label={t('Start Date')}
                                name={'startDate'}
                                rules={[{ required: true }]}
                            >
                                <DateTimeSplitInput />
                            </Form.Item>
                        )}
                    </Col>
                    <Col xs={24} xl={12}>
                        {screens.md ? (
                            <Form.Item
                                label={t('End Date')}
                                name={'endDate'}
                                rules={[{ required: true }]}
                            >
                                <DatePicker
                                    size={'small'}
                                    format={'DD-MM-YYYY HH:mm'}
                                    style={{ width: '100%' }}
                                    showTime={true}
                                    disabled={true}
                                    locale={Helper.Date.getLocale()}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label={t('End Date')}
                                name={'endDate'}
                            >
                                <div style={{
                                    padding: '6px 11px',
                                    background: '#f5f5f5',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 4,
                                    color: '#595959',
                                    fontSize: 14,
                                    minHeight: 36,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {appointment.endDate ? moment(appointment.endDate).format('DD-MM-YYYY HH:mm') : '—'}
                                </div>
                            </Form.Item>
                        )}
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Doctor')}
                            name={'doctorId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                size={'small'}
                            >
                                {doctors.map(x => {
                                    return (
                                        <Select.Option
                                            key={x.id}
                                            value={x.id}
                                        >
                                            {x.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6}>
                        <Form.Item
                            label={(
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {t('Institution')}
                                    {(
                                        (() => {
                                            if (Helper.Date.isBeforeToday(appointment.startDate)) {
                                                if (hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            } else {
                                                return true;
                                            }
                                        })()
                                    ) && (
                                            <Tooltip title={t('Institution')}>
                                                <Button
                                                    size={'small'}
                                                    type="default"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => {
                                                        openModal(t(''), (
                                                            <InstitutionCreateEdit
                                                                showFirmSettings={false}
                                                                institutionId={appointment.institution.id}
                                                                editable={false}
                                                            />
                                                        ), async () => { })
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                </div>
                            )}
                            name={'institutionId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                size={'small'}
                                showSearch={true}
                                disabled={
                                    Helper.Date.isBeforeToday(appointment.startDate) &&
                                    !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                }
                                open={institutionSelectOpen}
                                onDropdownVisibleChange={setInstitutionSelectOpen}
                                onInputKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setTimeout(() => {
                                            setInstitutionSelectOpen(false);
                                            if (e.target && typeof e.target.blur === 'function') {
                                                e.target.blur();
                                            }
                                        }, 0);
                                    }
                                }}
                                filterOption={(input, option) => {
                                    return option.children.toLocaleLowerCase(firmSettings?.locale).includes(input.toLocaleLowerCase(firmSettings?.locale))
                                }}
                            >
                                {institutions.map(x => {
                                    return (
                                        <Select.Option
                                            value={x.id}
                                            key={x.id}
                                        >
                                            {x.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6}>
                        <Form.Item
                            label={t('Appointment Type')}
                            name={'appointmentTypeId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <AppointmentTypeSelect
                                onTypes={(types) => {
                                    appointmentTypes.current = types
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Provizyon No')}
                            name={'provisionNo'}
                        >
                            <Input size="small" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        {
                            (hasPermission(PERMISSIONS.VIEW_PRICE)) && (<Form.Item
                                label={t('Private Note')}
                                name={'privateNote'}
                            >
                                <Input size="small" />
                            </Form.Item>)
                        }
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={'Anamnez'}
                            name={'anamnez'}
                        >
                            <Input.TextArea
                                rows={3}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Appointment Note')}
                            name={'note'}
                        >
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Form.Item>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography.Text strong={true} style={{ whiteSpace: 'nowrap' }}>{t('Check-in')}</Typography.Text>
                                    <Switch checked={appointment.checkIn} onChange={(e) => {
                                        setAppointment({
                                            ...appointment,
                                            checkIn: e,
                                        })
                                        onCheckedChange && onCheckedChange(e)
                                    }} />
                                </div>
                            </Form.Item>
                            <Form.Item>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Tooltip title={t('When off, this appointment is excluded from automatic E-Nabız sending.')}>
                                        <Typography.Text strong={true} style={{ whiteSpace: 'nowrap' }}>{t('Send E-Nabız')}</Typography.Text>
                                    </Tooltip>
                                    <Switch
                                        checked={appointment.sendEnabiz !== false}
                                        onChange={(e) => {
                                            setAppointment({
                                                ...appointment,
                                                sendEnabiz: e,
                                            })
                                        }}
                                    />
                                </div>
                            </Form.Item>
                            <Form.Item>
                                <StateChanger
                                    onStateChange={onStateChange}
                                    appointmentId={appointmentId}
                                    selectedStateId={appointment?.state?.id}
                                    checkIn={appointment.checkIn}
                                    onStates={(data) => {
                                        if (!data.find(x => x.id === appointment?.state?.id)) {
                                            setAppointment({
                                                ...appointment,
                                                state: {
                                                    id: data.find(x => x.stateGroup?.id === 1)?.id
                                                }
                                            })
                                        }
                                    }}
                                />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={12}>
                    </Col>
                </Row>
            </Form>
            </Card>
            <Card
                title={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FormOutlined style={{ color: operationsCardAccent }} />
                        <Typography.Text strong>
                            {t('Operations')} &amp; {t('Payments')}
                        </Typography.Text>
                    </div>
                )}
                style={{
                    backgroundColor: '#f5f5f5',
                    borderLeft: `4px solid ${operationsCardAccent}`,
                    ...cardCommon,
                }}
                headStyle={{ background: '#f5f5f5' }}
                bodyStyle={{ background: '#f5f5f5' }}
            >
                {/* Farklı bir birime kayıtlı teklif varsa ufak uyarı */}
                {!hasQuote && otherUnitQuote && (
                    <div style={{
                        padding: '6px 10px',
                        background: '#fafafa',
                        border: '1px dashed #d9d9d9',
                        borderRadius: 4,
                        marginBottom: 8,
                        fontSize: 12,
                        color: '#8c8c8c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flexWrap: 'wrap',
                    }}>
                        <span>ℹ️</span>
                        <span>
                            Hafızada farklı birime ait fiyat teklifi var
                            {otherUnitQuote.unitName && (
                                <> — <b style={{ color: '#595959' }}>
                                    {otherUnitQuote.firmName ? `${otherUnitQuote.firmName} / ` : ''}{otherUnitQuote.unitName}
                                </b></>
                            )}
                            {` (${otherUnitQuote.opCount} kalem)`}
                        </span>
                    </div>
                )}
                {/* --- AKTARILACAK FİYAT TEKLİFİ BANNERI --- */}
                {hasQuote && (() => {
                    const quote = PriceQuoteStorage.getForUnit(appointment?.unit?.id);
                    if (!quote) return null;
                    const opCount = (quote?.operationList?.length || 0) + (quote?.otherOperations?.length || 0);
                    return (
                        <div style={{
                            padding: '10px 12px',
                            background: '#fffbe6',
                            border: '1px solid #ffe58f',
                            borderRadius: 6,
                            marginBottom: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ fontSize: 18 }}>💾</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, fontSize: 13 }}>
                                    Hafızada fiyat teklifi var ({opCount} kalem)
                                </div>
                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                    Bu randevuya aktarmak için bir seçim yapın
                                </div>
                            </div>
                            <Button
                                size="middle"
                                type="primary"
                                icon={<ImportOutlined />}
                                onClick={() => transferQuote('replace')}
                            >
                                Aktar
                            </Button>
                            <Button
                                size="middle"
                                onClick={discardQuote}
                            >
                                Sil
                            </Button>
                        </div>
                    );
                })()}
                <div style={{ alignItems: 'start', gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                        <Tabs
                            size={'small'}
                            tabPosition={'top'}
                        >
                            <Tabs.TabPane
                                tab={'Operations'}
                                key={'operations'}
                            >
                                {screens.md ? (
                                    <Select
                                        style={{ width: '100%' }}
                                        size={'small'}
                                        id={'operation-id-list'}
                                        autoClearSearchValue={true}
                                        placeholder={t('Select Operation')}
                                        disabled={
                                            Helper.Date.isBeforeToday(appointment.startDate) &&
                                            !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                        }
                                        showSearch={true}
                                        filterOption={(input, option) => {
                                            return Helper.Text.normalizeText(option.children.toString()).includes(Helper.Text.normalizeText(input))
                                        }}
                                        mode={'multiple'}
                                        autoClearSearchValue={false}
                                        value={appointment?.appointmentOperations?.map(x => x.operationId)}
                                        onSelect={onSelectOperation}
                                        open={operationSelectOpen}
                                        onDropdownVisibleChange={(visible) => setOperationSelectOpen(visible)}
                                        onInputKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                setTimeout(() => {
                                                    setOperationSelectOpen(false);
                                                    if (e.target && typeof e.target.blur === 'function') {
                                                        e.target.blur();
                                                    }
                                                }, 0);
                                            }
                                        }}
                                        tagRender={(props) => (
                                            <Tag
                                                closable={true}
                                                style={{
                                                    marginRight: 3,
                                                    whiteSpace: 'normal',
                                                    textAlign: 'left',
                                                    wordBreak: 'break-word',
                                                    height: 'auto',
                                                    maxWidth: '100%',
                                                    display: 'inline-flex',
                                                    alignItems: 'center'
                                                }}
                                                onClose={(e) => {
                                                    if (Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) {
                                                        e.preventDefault();
                                                        return;
                                                    }
                                                    const ops = appointment?.appointmentOperations?.filter(x => x.operationId !== props.value);
                                                    setAppointment({ ...appointment, appointmentOperations: ops });
                                                    formRef.current.setFieldsValue({
                                                        endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
                                                    });
                                                }}
                                            >
                                                {props.label}
                                            </Tag>
                                        )}
                                    >
                                        {operationList.map(x => (
                                            <Select.Option key={x.id} value={x.id}>
                                                {`${x.name} - ${x.processMinute} ${t('minute')}`}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                ) : (
                                    <div>
                                        <Button
                                            block
                                            size="middle"
                                            icon={<PlusOutlined />}
                                            disabled={
                                                Helper.Date.isBeforeToday(appointment.startDate) &&
                                                !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                            }
                                            onClick={openOperationPicker}
                                        >
                                            {t('Select Operation')}
                                            {appointment?.appointmentOperations?.length > 0 && ` (${appointment.appointmentOperations.length} seçili)`}
                                        </Button>

                                        {/* iOS mobil klavye primer — user-gesture context'inde focus alır,
                                            modal mount edildiğinde real input'a transfer edilir */}
                                        <input
                                            ref={operationKeyboardPrimerRef}
                                            type="text"
                                            inputMode="search"
                                            aria-hidden="true"
                                            tabIndex={-1}
                                            readOnly
                                            style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                width: 1,
                                                height: 1,
                                                opacity: 0,
                                                pointerEvents: 'none',
                                                border: 'none',
                                                background: 'transparent',
                                                zIndex: -1,
                                            }}
                                        />

                                        <Modal
                                            title={(
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 24 }}>
                                                    <span>{t('Select Operation')}</span>
                                                    <Tag color="blue" style={{ margin: 0 }}>
                                                        {appointment?.appointmentOperations?.length || 0} seçili
                                                    </Tag>
                                                </div>
                                            )}
                                            open={operationPickerOpen}
                                            onCancel={() => setOperationPickerOpen(false)}
                                            footer={null}
                                            width="100%"
                                            style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
                                            bodyStyle={{
                                                padding: 12,
                                                height: 'calc(100vh - 110px)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'hidden'
                                            }}
                                            destroyOnClose
                                        >
                                            <Input
                                                ref={operationPickerSearchRef}
                                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                                placeholder={t('Search')}
                                                size="middle"
                                                value={operationPickerSearch}
                                                onChange={(e) => setOperationPickerSearch(e.target.value)}
                                                allowClear
                                                autoFocus
                                                style={{ marginBottom: 8 }}
                                            />
                                            <div style={{
                                                flex: 1,
                                                overflowY: 'auto',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: 4,
                                                marginBottom: 8
                                            }}>
                                                {operationList
                                                    .filter(x => !operationPickerSearch || Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(operationPickerSearch)))
                                                    .map(item => {
                                                        const selected = appointment?.appointmentOperations?.some(x => x.operationId === item.id);
                                                        return (
                                                            <div
                                                                key={item.id}
                                                                onClick={() => {
                                                                    if (selected) {
                                                                        const ops = appointment.appointmentOperations.filter(x => x.operationId !== item.id);
                                                                        setAppointment({ ...appointment, appointmentOperations: ops });
                                                                        formRef.current.setFieldsValue({
                                                                            endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
                                                                        });
                                                                    } else {
                                                                        onSelectOperation(item.id);
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '12px 10px',
                                                                    borderBottom: '1px solid #f5f5f5',
                                                                    backgroundColor: selected ? '#e6f7ff' : 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 10,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <div style={{
                                                                    width: 22, height: 22,
                                                                    border: `2px solid ${selected ? '#1890ff' : '#d9d9d9'}`,
                                                                    borderRadius: 4,
                                                                    background: selected ? '#1890ff' : 'white',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    color: 'white', fontSize: 14, lineHeight: 1, flexShrink: 0
                                                                }}>
                                                                    {selected && '✓'}
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div style={{ fontWeight: selected ? 600 : 400, fontSize: 14, wordBreak: 'break-word' }}>
                                                                        {item.name}
                                                                    </div>
                                                                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                                                                        {item.processMinute} {t('minute')}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                {operationList.filter(x => !operationPickerSearch || Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(operationPickerSearch))).length === 0 && (
                                                    <Empty
                                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        description={t('No Data')}
                                                        style={{ padding: 24 }}
                                                    />
                                                )}
                                            </div>
                                            <Button
                                                type="primary"
                                                block
                                                size="large"
                                                onClick={() => setOperationPickerOpen(false)}
                                            >
                                                Tamam ({appointment?.appointmentOperations?.length || 0})
                                            </Button>
                                        </Modal>
                                    </div>
                                )}

                                <div>
                                    {!screens.md ? renderMobileOperations() : (
                                        <Table
                                            style={{ marginTop: 8 }}
                                            scroll={{ x: 600 }}
                                            size={'small'}
                                            dataSource={appointment.appointmentOperations}
                                            pagination={false}
                                            rowKey={'id'}
                                            columns={[
                                                {
                                                    key: 'name',
                                                    dataIndex: 'name',
                                                    title: t('Name'),
                                                    render: (text, row) => {
                                                        const ops = operationList.find(x => x.id === row.operationId)
                                                        if (ops && ops.subItems?.length > 0) {
                                                            return (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 8
                                                                }}>
                                                                    <Tooltip
                                                                        title={(
                                                                            <>
                                                                                {row?.subItems?.length === 0 && t('No Sub Operations')}
                                                                                <table>
                                                                                    {
                                                                                        row?.subItems?.map(subItem => (
                                                                                            <tr>
                                                                                                <td>{subItem.name}</td>
                                                                                                <td>
                                                                                                    <ColorDot />
                                                                                                </td>
                                                                                                <td>{Helper.Currency.format(subItem.price, { currency })}</td>
                                                                                            </tr>
                                                                                        ))
                                                                                    }
                                                                                </table>
                                                                            </>
                                                                        )}
                                                                    >
                                                                        {row?.operation?.name}
                                                                    </Tooltip>
                                                                    <Button
                                                                        icon={<SmallDashOutlined />}
                                                                        size={'small'}
                                                                        onClick={() => {
                                                                            openModal(
                                                                                t(''),
                                                                                <AppointmentOperationSubItemsEdit
                                                                                    currency={currency}
                                                                                    subItems={ops.subItems}
                                                                                    selectedSubItems={row.subItems ?? []}
                                                                                    onSave={(items) => {
                                                                                        const ops = appointment.appointmentOperations.map(x => {
                                                                                            if (x.id === row.id) {
                                                                                                return {
                                                                                                    ...x,
                                                                                                    price: items.reduce((prev, val) => prev += val.price, 0),
                                                                                                    subItems: items
                                                                                                }
                                                                                            }
                                                                                            return x
                                                                                        })
                                                                                        setAppointment({
                                                                                            ...appointment,
                                                                                            appointmentOperations: ops
                                                                                        })
                                                                                        closeModal()
                                                                                    }}
                                                                                />,
                                                                                () => { }
                                                                            )
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                        return row?.operation?.name
                                                    }
                                                },
                                                {
                                                    key: 'accessionNumber',
                                                    dataIndex: 'accessionNumber',
                                                    title: (<>{t('Accession Number')} / <br /> {t('Process Minute')}</>),
                                                    width: 225,
                                                    render: (text, row) => (
                                                        <div>
                                                            <Input
                                                                style={{ width: '100%' }}
                                                                size={'small'}
                                                                value={text}
                                                                onChange={(e) => {
                                                                    updateOperationData(row.id, 'accessionNumber', e.target.value)
                                                                }}
                                                            />
                                                            <InputNumber
                                                                style={{ width: '100%' }}
                                                                size={'small'}
                                                                defaultValue={row?.processMinute}
                                                                value={row?.processMinute}
                                                                onChange={(val) => {
                                                                    updateOperationData(row.id, 'processMinute', val)
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'newPrice',
                                                    dataIndex: 'newPrice',
                                                    width: 300,
                                                    title: (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            {t('Patient Op. Price')}
                                                            <TitleCurrencySelector
                                                                value={currency}
                                                                disabled={Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)}
                                                                onChange={(e) => {
                                                                    updateCurrency(e)
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                                    render: (text, row) => {
                                                        const ops = operationList.find(x => x.id === row.operationId)
                                                        return (
                                                            <div>
                                                                <InputNumber
                                                                    // disabled={ops?.subItems?.length > 0}
                                                                    disabled={
                                                                        Helper.Date.isBeforeToday(appointment.startDate) &&
                                                                        !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                                                    }
                                                                    style={{ width: '100%' }}
                                                                    size={'small'}
                                                                    prefix={Currency[appointment.currency]}
                                                                    value={row?.price}
                                                                    step={0.01}
                                                                    onChange={(newPrice) => {
                                                                        if (ops?.subItems?.length > 0) {
                                                                            const updatedOperations = appointment.appointmentOperations.map(op => {
                                                                                if (op.id === row.id) {
                                                                                    const subItemsPrice = op.subItems.reduce((sum, item) => sum + item.price, 0)
                                                                                    const ratio = newPrice / subItemsPrice

                                                                                    op.price = newPrice
                                                                                    op.subItems = op.subItems.map(sub => ({
                                                                                        ...sub,
                                                                                        price: parseFloat((sub.price * ratio).toFixed(2))
                                                                                    }))

                                                                                    // Alt işlemler toplamı ile yeni fiyat arasında ufak fark varsa kapat
                                                                                    const newSubItemsPrice = op.subItems.reduce((sum, item) => sum + item.price, 0)
                                                                                    const diff = newPrice - newSubItemsPrice
                                                                                    if (Math.abs(diff) > 0.01 && op.subItems.length > 0) {
                                                                                        op.subItems[0].price = parseFloat((op.subItems[0].price + diff).toFixed(2))
                                                                                    }

                                                                                    return { ...op }
                                                                                }
                                                                                return op
                                                                            })

                                                                            setAppointment({
                                                                                ...appointment,
                                                                                appointmentOperations: updatedOperations
                                                                            })
                                                                        } else {
                                                                            updateOperationData(row.id, 'price', newPrice)
                                                                        }
                                                                    }}

                                                                />
                                                                <table>
                                                                    <tr>
                                                                        <td style={{ width: 100 }}>
                                                                            <Typography.Text>
                                                                                <b>{t('Price')}</b>
                                                                            </Typography.Text>
                                                                        </td>
                                                                        <td>
                                                                            <Typography.Text strong>
                                                                                {Helper.Currency.format(row?.operation?.price)}
                                                                            </Typography.Text>
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ width: 100 }}>
                                                                            <Typography.Text>
                                                                                <b>{t('Institution')} / {t('Special')}</b>
                                                                            </Typography.Text>
                                                                        </td>
                                                                        <td>
                                                                            <div style={{ width: '100%', display: 'flex', gap: 4 }}>
                                                                                <Typography.Text>
                                                                                    {Helper.Currency.format(getInstitutionPrice(row?.operationId))}
                                                                                </Typography.Text>
                                                                                {' / '}
                                                                                <Typography.Text>
                                                                                    {Helper.Currency.format(getAppointmentTypePrice(row?.operationId))}
                                                                                </Typography.Text>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                </table>
                                                            </div>
                                                        )
                                                    }
                                                },
                                                {
                                                    key: '#',
                                                    dataIndex: '#',
                                                    title: '#',
                                                    width: 50,
                                                    render: (text, row) => (
                                                        <Button
                                                            size={'small'}
                                                            danger
                                                            disabled={
                                                                Helper.Date.isBeforeToday(appointment.startDate) &&
                                                                !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                                            }
                                                            onClick={() => {
                                                                setAppointment({
                                                                    ...appointment,
                                                                    appointmentOperations: appointment.appointmentOperations.filter(x => x.id !== row.id),
                                                                })
                                                            }}>
                                                            X
                                                        </Button>
                                                    )
                                                }
                                            ]}
                                            summary={() => getPaymentSummary(4)}
                                        />
                                    )}
                                </div>
                            </Tabs.TabPane>
                            <Tabs.TabPane
                                key={'extra'}
                                tab={t('Extra')}
                            >
                                <>
                                    <Form
                                        size={'small'}
                                        ref={formOtherOperationRef}
                                        layout={!screens.md ? 'vertical' : 'inline'}
                                        onFinish={onOtherOperationFinish}
                                    >
                                        <Form.Item
                                            name={'name'}
                                            rules={[{ required: true }]}
                                            style={!screens.md ? { marginBottom: 8 } : {}}
                                        >
                                            <Input placeholder={t('Name')} />
                                        </Form.Item>
                                        <Form.Item
                                            name={'price'}
                                            rules={[{ required: true, message: '' }]}
                                            style={!screens.md ? { marginBottom: 8 } : {}}
                                        >
                                            <InputNumber
                                                style={!screens.md ? { width: '100%' } : { minWidth: 200 }}
                                                placeholder={t('Price')}
                                                min={0}
                                                step={0.01}
                                                prefix={Currency[currency]}
                                            />
                                        </Form.Item>
                                        <Form.Item style={!screens.md ? { marginBottom: 8 } : {}}>
                                            <Button
                                                className={'bg-warning'}
                                                htmlType={'submit'}
                                                style={!screens.md ? { width: '100%' } : {}}
                                            >
                                                +
                                            </Button>
                                        </Form.Item>
                                    </Form>

                                    {!screens.md ? renderMobileExtraOperations() : (
                                        <Table
                                            size={'small'}
                                            rowKey={'id'}
                                            dataSource={appointment.appointmentOtherOperations}
                                            pagination={false}
                                            columns={[
                                                { key: 'name', dataIndex: 'name', title: t('Name') },
                                                {
                                                    key: 'price',
                                                    dataIndex: 'price',
                                                    title: t('Price'),
                                                    render: (text, row) => Helper.Currency.format(text, { currency })
                                                },
                                                {
                                                    key: '#',
                                                    dataIndex: '#',
                                                    title: t('#'),
                                                    render: (text, row) => <Button size={'small'}
                                                        onClick={() => deleteOtherOperation(row)}
                                                        danger={true}>X</Button>
                                                },
                                            ]}
                                            summary={() => getPaymentSummary(3)}
                                        />
                                    )}
                                </>
                            </Tabs.TabPane>
                        </Tabs>
                    </div>

                </div>
                {/* Kurum notu Operations kartının en altında — kullanıcı işlemler bölümüyle
                    birlikte görsün */}
                <div
                    dangerouslySetInnerHTML={{ __html: appointment.institution.note ?? '------' }}
                    style={{ marginTop: 8 }}
                />
            </Card>

            {!screens.md && (
                <div style={{ display: 'flex', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
                    {renderActionButtons()}
                </div>
            )}
        </>
    )
}

const TitleCurrencySelector = ({ value, onChange, disabled }) => {
    const [val, setVal] = useState(value)

    useEffect(() => {
        onChange(val)
    }, [val]);

    useEffect(() => {
        if (value !== val) {
            setVal(value)
        }
    }, [value]);

    return (
        <CurrencySelect
            placeholder={''}
            style={{ width: 100 }}
            disabled={disabled}
            size={'small'}
            value={val}
            onChange={(e) => setVal(e)}
        />
    )
}